// PC queue (R-S4, spec speakers P0-12/P0-13, ITConf «Очередь ПК»): summary of the event's talks, problem filters, bulk curator and status actions.

import { useMemo, useState } from 'react';
import { canRead, sortRecords, type Id, type RecordObj, type ViewColumn } from '@confdeck/core-shared';
import { Button, Chip, DataTable, Tabs, TopBar, TopTitle, useAccess, Menu, MenuContent, MenuItem, MenuTrigger, cx, navigate, useMeta, useScope, useSync, useToast, useTypesTick } from '@confdeck/core-web';
import s from './conferences.module.css';

type Problem = 'all' | 'mine' | 'unsorted' | 'no_curator' | 'no_email' | 'unscheduled' | 'speaker_comments';
const PROBLEMS: { key: Problem; label: string }[] = [
  { key: 'all', label: 'Все' }, { key: 'mine', label: 'Мои' }, { key: 'unsorted', label: 'Неразобранные' }, { key: 'no_curator', label: 'Нет куратора' },
  { key: 'no_email', label: 'Докладчик без email' }, { key: 'unscheduled', label: 'Принят, нет в программе' }, { key: 'speaker_comments', label: 'Есть комментарии для докладчика' },
];
const COLUMNS: ViewColumn[] = ['title', 'speakers', 'status', 'curator', 'score', 'comments_count', 'createdAt'].map((f) => ({ field: f, width: f === 'title' ? 320 : f === 'speakers' ? 220 : 150 }));

export function PcQueue() {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const [eventId] = useScope();
  const talkT = meta.typeByKey('talk'), reviewT = meta.typeByKey('review'), slotT = meta.typeByKey('slot'), speakerT = meta.typeByKey('speaker');
  useTypesTick([talkT?.id, reviewT?.id, slotT?.id, speakerT?.id].filter((x): x is string => !!x));
  const [problem, setProblem] = useState<Problem>('all');
  const [selected, setSelected] = useState<Set<Id>>(new Set());
  const [sort, setSort] = useState([{ field: 'createdAt', dir: 'asc' as 'asc' | 'desc' }]);
  const [columns, setColumns] = useState(COLUMNS);
  const me = client.header?.userId;
  // A blind reviewer does not read speakers: the speaker filter would mislead them.
  const access = useAccess();
  const speakersVisible = !!speakerT && canRead(access, speakerT.id);
  const programVisible = !!slotT && canRead(access, slotT.id);
  const term = (k: string) => meta.terms(meta.field(talkT?.id ?? '', 'status')?.options.taxonomyId ?? '').find((t) => t.key === k)?.id;

  const data = useMemo(() => {
    if (!talkT || !eventId) return null;
    const f = (tid: string | undefined, key: string) => (tid ? meta.field(tid, key) : undefined);
    const talks: RecordObj[] = [];
    for (const id of client.store.recordIds(talkT.id)) { const r = client.store.record(id); if (r && client.store.linked(r, f(talkT.id, 'event')!).includes(eventId)) talks.push(r); }
    const scheduled = new Set<Id>(), speakerComments = new Map<Id, number>();
    if (slotT) for (const id of client.store.recordIds(slotT.id)) { const r = client.store.record(id); if (r) for (const t of client.store.linked(r, f(slotT.id, 'talk')!)) scheduled.add(t); }
    const toSpeaker = reviewT ? meta.terms(meta.field(reviewT.id, 'visibility')?.options.taxonomyId ?? '').find((t) => t.key === 'speaker')?.id : undefined;
    if (reviewT) for (const id of client.store.recordIds(reviewT.id)) {
      const r = client.store.record(id);
      if (r && r.data.visibility === toSpeaker) for (const t of client.store.linked(r, f(reviewT.id, 'talk')!)) speakerComments.set(t, (speakerComments.get(t) ?? 0) + 1);
    }
    const noEmail = (t: RecordObj) => client.store.linked(t, f(talkT.id, 'speakers')!).some((sid) => !client.store.record(sid)?.data.email) || !client.store.linked(t, f(talkT.id, 'speakers')!).length;
    const st = (t: RecordObj, ...keys: string[]) => keys.some((k) => t.data.status === term(k));
    const open = (t: RecordObj) => st(t, 'new', 'discussion', 'revision');
    const is: Record<Problem, (t: RecordObj) => boolean> = {
      all: () => true, mine: (t) => t.data.curator === me || (Array.isArray(t.data.co_curators) && t.data.co_curators.includes(me)),
      unsorted: (t) => st(t, 'new'), no_curator: (t) => open(t) && !t.data.curator, no_email: noEmail,
      unscheduled: (t) => st(t, 'accepted') && !scheduled.has(t.id), speaker_comments: (t) => (speakerComments.get(t.id) ?? 0) > 0,
    };
    const scores = talks.map((t) => Number(t.data.score)).filter((x) => x > 0);
    return {
      talks, is,
      counts: Object.fromEntries(PROBLEMS.map((p) => [p.key, talks.filter(is[p.key]).length])) as Record<Problem, number>,
      tiles: [
        { label: 'Всего докладов', value: talks.length },
        { label: 'Неразобранных', value: talks.filter((t) => st(t, 'new')).length },
        { label: 'На обсуждении', value: talks.filter((t) => st(t, 'discussion', 'revision')).length },
        { label: 'Принято и опубликовано', value: talks.filter((t) => st(t, 'accepted', 'published')).length },
        { label: 'Без куратора', value: talks.filter((t) => open(t) && !t.data.curator).length },
        { label: 'Средний балл', value: scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '—' },
        { label: 'Комментариев ПК', value: talks.reduce((a, t) => a + Number(t.data.comments_count ?? 0), 0) },
      ],
    };
  }, [talkT, reviewT, slotT, eventId, client.store, meta, me, client.store.typeVersion.get(talkT?.id ?? ''), client.store.typeVersion.get(reviewT?.id ?? ''), client.store.typeVersion.get(slotT?.id ?? '')]);

  if (!talkT) return <div className={s.empty}>Нет раздела докладов</div>;
  if (!eventId || !data) return <div className={s.empty}>Выберите событие внизу левой колонки.</div>;
  // The table shows ids in the given order; sorting is done here with the shared comparator.
  const ids = sortRecords(client.store, talkT.id, sort, data.talks.filter(data.is[problem])).map((t) => t.id);

  /** Runs a command over the selection and reports like ITConf: done, skipped and the first errors. */
  const bulk = async (label: string, command: string, extra: Record<string, unknown> = {}) => {
    let ok = 0; const errors: string[] = [];
    for (const id of selected) { const r = await client.run(command, { id, ...extra }); if (r.ok) ok++; else errors.push(r.error.message); }
    setSelected(new Set());
    toast({ kind: errors.length && !ok ? 'error' : 'info', text: `${label}: ${ok}.${errors.length ? ` Пропущено: ${errors.length}. ${[...new Set(errors)].slice(0, 3).join('; ')}` : ''}` });
  };
  const statuses = meta.terms(meta.field(talkT.id, 'status')?.options.taxonomyId ?? '').filter((t) => t.parentId);

  return (
    <div className={s.queue}>
      <TopBar start={<TopTitle icon="check">Очередь ПК</TopTitle>} count={ids.length} />
      <div className={s.tiles}>{data.tiles.map((t) => <div key={t.label} className={s.tile}><b>{t.value}</b><span>{t.label}</span></div>)}</div>
      <div className={s.queueBar}>
        <Tabs label="Диагностика" className={s.problems} value={problem} onChange={(k) => { setProblem(k); setSelected(new Set()); }}
          items={PROBLEMS.filter((p) => (speakersVisible || p.key !== 'no_email') && (programVisible || p.key !== 'unscheduled')).map((p) => ({ key: p.key, label: p.label, count: data.counts[p.key] ?? 0 }))} />
        {selected.size > 0 && (
          <div className={s.bulk}>
            <span>Выбрано: {selected.size}</span>
            <Button size="sm" variant="secondary" icon="user" onClick={() => void bulk('Доклады взяты в работу', 'talk.take')}>Взять в работу</Button>
            <Button size="sm" variant="secondary" onClick={() => void bulk('Кураторство обновлено, вы назначены основным', 'talk.takePrimary')}>Сделать себя основным</Button>
            <Button size="sm" variant="secondary" onClick={() => void bulk('Вы сняли себя с выбранных докладов', 'talk.release')}>Снять себя</Button>
            <Menu>
              <MenuTrigger asChild><Button size="sm" variant="secondary" icon="stage">Перевести в статус</Button></MenuTrigger>
              <MenuContent>{statuses.map((t) => <MenuItem key={t.id} onSelect={() => void bulk('Статус ревью обновлён', 'talk.setStatus', { status: t.id })}><Chip color={t.color} dot>{t.name}</Chip></MenuItem>)}</MenuContent>
            </Menu>
          </div>
        )}
      </div>
      {ids.length ? (
        <DataTable typeId={talkT.id} ids={ids} columns={columns} onColumns={setColumns} sortState={sort} onSort={(field, dir) => setSort(dir ? [{ field, dir }] : [])}
          onFilterBy={() => undefined} onAddField={() => undefined} onOpen={(id) => navigate(`/r/${id}`)} openId={null} selected={selected} onSelect={setSelected} />
      ) : <div className={s.empty}>{problem === 'all' ? 'У события пока нет докладов' : 'Таких докладов нет'}</div>}
    </div>
  );
}
